import { useResponsive } from '@/hooks/use-responsive';
import { useThemeColor } from '@/hooks/use-theme-color';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/contexts/AuthContext';
import { UserAvatar } from '@/components/UserAvatar';
import { FiCalendar, FiInfo, FiMessageCircle, FiSettings } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { tourTarget } from '@/features/app-tour';
import './BottomNavigation.css';

const NAV_TOUR_IDS: Record<string, string> = {
  eventos: 'nav-eventos',
  chats: 'nav-chats',
  perfil: 'nav-perfil',
  ajustes: 'nav-ajustes',
};

export function BottomNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { t } = useLanguage();
  const { currentUserProfile } = useAuth();

  const tealColor = useThemeColor({}, 'teal');

  // Perfil siempre como última opción del menú inferior
  const navItems = useMemo(() => {
    return [
      { name: 'eventos', label: t('navigation.eventos'), icon: FiCalendar, route: '/eventos' },
      { name: 'chats', label: t('navigation.chats'), icon: FiMessageCircle, route: '/chats' },
      { name: 'ajustes', label: t('navigation.ajustes'), icon: FiSettings, route: '/ajustes' },
      { name: 'quienes-somos', label: t('navigation.quienesSomos'), icon: FiInfo, route: '/quienes-somos' },
      { name: 'perfil', label: t('navigation.perfil'), icon: null, route: '/perfil' },
    ];
  }, [t]);

  const currentRoute = location.pathname.split('/').filter(Boolean)[0] || 'eventos';

  const isRouteActive = (itemName: string) => {
    if (itemName === 'eventos') {
      return currentRoute === 'eventos' || currentRoute === '' || location.pathname === '/';
    }
    if (itemName === 'chats') {
      return currentRoute === 'chats' || currentRoute === 'actividad';
    }
    return currentRoute === itemName;
  };

  if (!isMobile) {
    return null;
  }

  return (
    <nav className="bottom-navigation">
      <div className="bottom-navigation-capsule">
        <div className="bottom-navigation-content">
          {navItems.map((item) => {
            const isActive = isRouteActive(item.name);
            const IconComponent = item.icon;
            return (
              <button
                key={item.name}
                className={`bottom-navigation-item ${isActive ? 'bottom-navigation-item-active' : ''}`}
                onClick={() => navigate(item.route)}
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
                {...(NAV_TOUR_IDS[item.name] ? tourTarget(NAV_TOUR_IDS[item.name]) : {})}
              >
                <div className="bottom-navigation-item-wrapper">
                  {isActive && (
                    <span className="bottom-navigation-active-pill" aria-hidden="true" />
                  )}
                  <div className="bottom-navigation-item-inner">
                    {item.name === 'perfil' ? (
                      <UserAvatar
                        size={22}
                        name={currentUserProfile?.name}
                        email={currentUserProfile?.email}
                        avatarUrl={currentUserProfile?.avatar}
                        className={
                          isActive
                            ? 'bottom-navigation-profile-avatar bottom-navigation-profile-avatar--active'
                            : 'bottom-navigation-profile-avatar'
                        }
                      />
                    ) : IconComponent ? (
                      <IconComponent
                        className="bottom-navigation-icon"
                        size={17}
                        color={isActive ? tealColor : 'currentColor'}
                      />
                    ) : null}
                    <span className="bottom-navigation-label">
                      {item.label}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
